import { createAtom } from '@xstate/store';

import { AudioFeatureType } from '../../api/cc-smarts/models/AudioFeature';
import type { ActiveContent } from '../../types/activeContent';
import { type CustomizationType, CustomLength, CustomTone } from '../../types/customizationTypes';
import type { AudioFeatureMenuItem } from '../ui/audio-player/menu/AudioFeatureMenuGroup';

const DEFAULT_AUDIO_FEATURE = AudioFeatureType.BRIEFING;

export type AudioPlayerState = {
	activeContent: ActiveContent;
	activeFeature: AudioFeatureType;
	activeMenu?: AudioFeatureMenuItem;
	pendingContent: ActiveContent | null;
	isOpen: boolean;
	isPlaying: boolean;
	isPaused: boolean;
	product: string;
	cloudId: string;
	activationId: string;
	customization: CustomizationType;
};

const DEFAULT_CUSTOMIZATION: CustomizationType = {
	tone: CustomTone.PROFESSIONAL,
	length: CustomLength.SHORT,
};

const initialState: AudioPlayerState = {
	activeContent: {
		contentId: '',
		spaceKey: '',
		contentType: '',
		title: '',
		author: '',
		avatarUrl: '',
	},
	activeFeature: DEFAULT_AUDIO_FEATURE,
	pendingContent: null,
	isOpen: false,
	isPlaying: false,
	isPaused: false,
	product: '',
	cloudId: '',
	activationId: '',
	customization: DEFAULT_CUSTOMIZATION,
};

export const audioPlayerState = createAtom<AudioPlayerState>(initialState);

export const setActiveContent = (activeContent: ActiveContent) => {
	audioPlayerState.set((prevState) => ({
		...prevState,
		activeContent,
		// Reset playback states when switching to a new track
		isPlaying: false,
		isPaused: false,
	}));
};

export const setActiveFeature = (activeFeature: AudioFeatureType) => {
	audioPlayerState.set((prevState) => ({ ...prevState, activeFeature }));
};

export const setActiveMenu = (activeMenu: AudioFeatureMenuItem | undefined) => {
	audioPlayerState.set((prevState) => ({ ...prevState, activeMenu }));
};

export const openAudioPlayer = (contentToOpen: ActiveContent) => {
	audioPlayerState.set((prevState) => {
		// Check if the content is already active
		const isAlreadyActive = prevState.activeContent.contentId === contentToOpen.contentId;

		// Check if the content is already pending
		const isAlreadyPending = prevState.pendingContent?.contentId === contentToOpen.contentId;

		// If content is already active or pending, do nothing
		if (isAlreadyActive || isAlreadyPending) {
			return prevState;
		}

		// If player is already open, set new content as pending
		if (prevState.isOpen) {
			return { ...prevState, pendingContent: contentToOpen };
		}
		// Otherwise, set as active content and open player
		return { ...prevState, activeContent: contentToOpen, isOpen: true };
	});
};

export const activatePendingContent = () => {
	audioPlayerState.set((prevState) => {
		if (prevState.pendingContent) {
			return {
				...prevState,
				activeContent: prevState.pendingContent,
				pendingContent: null,
				isPlaying: false,
				isPaused: false,
			};
		}
		return prevState;
	});
};

export const clearPendingContent = () => {
	audioPlayerState.set((prevState) => ({
		...prevState,
		pendingContent: null,
	}));
};

export const closeAudioPlayer = () => {
	audioPlayerState.set(initialState);
};

export const setIsPlaying = (isPlaying: boolean) => {
	audioPlayerState.set((prevState) => ({ ...prevState, isPlaying }));
};

export const setIsPaused = (isPaused: boolean) => {
	audioPlayerState.set((prevState) => ({ ...prevState, isPaused }));
};

// Convenience action to update multiple playback properties at once
export const updatePlayerState = (updates: Partial<AudioPlayerState>) => {
	audioPlayerState.set((prevState) => ({ ...prevState, ...updates }));
};

export const setCustomization = (newCustomization: CustomizationType) => {
	audioPlayerState.set((prevState) => {
		const { customization } = prevState;
		const updatedCustomization = { ...customization, ...newCustomization };

		return { ...prevState, customization: updatedCustomization };
	});
};
